import { useEffect, useRef, useState } from "react";
import { Star, MessageSquarePlus, BadgeCheck, ImagePlus, X, CornerDownRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, photoUrl } from "@/lib/api";

function Stars({ value, size = "w-4 h-4" }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= Math.round(value) ? "fill-accent text-accent" : "text-muted-foreground/30"}`}
        />
      ))}
    </span>
  );
}

export default function ProductReviews({ slug, onSummary }) {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: 0, distribution: {} });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", rating: 5, title: "", body: "" });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get(`/products/${slug}/reviews`).then((r) => {
      setReviews(r.data.reviews || []);
      setSummary(r.data.summary || { count: 0, average: 0, distribution: {} });
      onSummary?.(r.data.summary);
    }).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (!files.length) return;
    const room = 3 - photos.length;
    if (room <= 0) { toast.error("You can add up to 3 photos"); return; }
    setUploading(true);
    for (const file of files.slice(0, room)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/reviews/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => (prev.length < 3 ? [...prev, data.path] : prev));
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not upload photo");
      }
    }
    setUploading(false);
  };

  const removePhoto = (path) => setPhotos((prev) => prev.filter((p) => p !== path));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${slug}/reviews`, { ...form, photos });
      toast.success(data.message);
      setForm({ name: "", email: "", rating: 5, title: "", body: "" });
      setPhotos([]);
      setShowForm(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit review");
    } finally { setBusy(false); }
  };

  return (
    <section id="product-reviews" className="mt-20 border-t border-border pt-10" data-testid="product-reviews">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-display font-bold tracking-tight">Customer reviews</h2>
          {summary.count > 0 ? (
            <div className="mt-2 flex items-center gap-2" data-testid="reviews-summary">
              <Stars value={summary.average} />
              <span className="text-sm font-semibold">{summary.average.toFixed(1)} out of 5</span>
              <span className="text-sm text-muted-foreground">· {summary.count} review{summary.count === 1 ? "" : "s"}</span>
            </div>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground" data-testid="reviews-empty">No reviews yet — be the first to review this product.</p>
          )}
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          data-testid="write-review-btn"
          className="inline-flex items-center gap-2 border border-primary text-primary px-4 py-2.5 rounded-sm font-semibold hover:bg-primary hover:text-white transition-colors">
          <MessageSquarePlus className="w-4 h-4" /> Write a review
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-card border border-border rounded-sm p-6 space-y-4 mb-10" data-testid="review-form">
          <div>
            <span className="text-sm font-medium">Your rating</span>
            <div className="mt-1.5 flex items-center gap-1" data-testid="rating-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setForm((f) => ({ ...f, rating: n }))} data-testid={`rating-star-${n}`} className="p-0.5">
                  <Star className={`w-7 h-7 transition-colors ${n <= form.rating ? "fill-accent text-accent" : "text-muted-foreground/30 hover:text-accent"}`} />
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium">Name</span>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required maxLength={80} data-testid="review-name"
                className="mt-1.5 w-full h-11 px-3 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Email <span className="text-muted-foreground font-normal">(optional, not shown)</span></span>
              <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="review-email"
                className="mt-1.5 w-full h-11 px-3 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
            </label>
          </div>
          <label className="block">
            <span className="text-sm font-medium">Title <span className="text-muted-foreground font-normal">(optional)</span></span>
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} maxLength={120} data-testid="review-title"
              className="mt-1.5 w-full h-11 px-3 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Your review</span>
            <textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} required rows={4} maxLength={2000} data-testid="review-body"
              className="mt-1.5 w-full px-3 py-2.5 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
          </label>
          <div>
            <span className="text-sm font-medium">Photos <span className="text-muted-foreground font-normal">(optional, up to 3)</span></span>
            <div className="mt-1.5 flex flex-wrap items-center gap-3">
              {photos.map((p) => (
                <div key={p} className="relative w-20 h-20 rounded-sm overflow-hidden border border-border" data-testid="review-photo-preview">
                  <img src={photoUrl(p)} alt="review" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(p)} data-testid="remove-photo-btn"
                    className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {photos.length < 3 && (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="add-photo-btn"
                  className="w-20 h-20 grid place-items-center border border-dashed border-border rounded-sm text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-50 transition-colors">
                  <span className="flex flex-col items-center gap-1"><ImagePlus className="w-5 h-5" /><span className="text-[10px]">{uploading ? "Uploading…" : "Add"}</span></span>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickPhotos} className="hidden" data-testid="review-photo-input" />
            </div>
          </div>
          <button type="submit" disabled={busy || uploading} data-testid="review-submit"
            className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors">
            {busy ? "Submitting…" : "Submit review"}
          </button>
          <p className="text-xs text-muted-foreground">Reviews are checked before they appear.</p>
        </form>
      )}

      {reviews.length > 0 && (
        <div className="space-y-6" data-testid="reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="border-b border-border/60 pb-6" data-testid="review-item">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Stars value={r.rating} />
                <span className="text-xs text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : ""}</span>
              </div>
              {r.title && <p className="mt-2 font-semibold text-sm">{r.title}</p>}
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{r.body}</p>
              {r.photos?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="review-photos">
                  {r.photos.map((p) => (
                    <a key={p} href={photoUrl(p)} target="_blank" rel="noreferrer" className="block w-24 h-24 rounded-sm overflow-hidden border border-border hover:border-primary transition-colors">
                      <img src={photoUrl(p)} alt="customer photo" loading="lazy" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
              <p className="mt-2 text-xs font-medium flex items-center gap-1.5">
                {r.name}
                {r.verified && (
                  <span className="inline-flex items-center gap-1 text-primary font-semibold" data-testid="verified-buyer-badge">
                    <BadgeCheck className="w-3.5 h-3.5" /> Verified purchase
                  </span>
                )}
              </p>
              {r.admin_reply && (
                <div className="mt-3 ml-4 border-l-2 border-primary/30 pl-4 py-1" data-testid="review-admin-reply">
                  <span className="flex items-center gap-1.5 text-overline text-primary"><CornerDownRight className="w-3.5 h-3.5" /> Response from Hydronic Heating</span>
                  <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">{r.admin_reply}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
